import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserPreferences {
  id?: string;
  primary_use_case: string;
  platform_priority: string[];
  dashboard_layout: string;
  content_filters: string[];
  monitoring_keywords: string[];
  completed_onboarding: boolean;
}

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchPreferences();
    } else {
      setLoading(false);
    }
    // Depend on user.id (stable string), not the user object — AuthContext
    // produces a new User object reference on every auth event, so depending
    // on `user` re-fires this effect on every event and feeds the React
    // error #185 loop on /dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      console.log('Fetching preferences for user:', user.id);
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('Preferences query result:', { data, error });

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching preferences:', error);
      } else {
        console.log('Setting preferences:', data);
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (prefs: Omit<UserPreferences, 'id'>) => {
    if (!user) return;

    try {
      console.log('Saving preferences for user:', user.id, prefs);
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...prefs
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving preferences:', error);
        throw error;
      }

      console.log('Preferences saved successfully:', data);
      setPreferences(data);
      return data;
    } catch (error) {
      console.error('Error saving preferences:', error);
      throw error;
    }
  };

  const updateOnboardingStatus = async (completed: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_preferences')
        .update({ completed_onboarding: completed })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating onboarding status:', error);
        throw error;
      }

      setPreferences(prev => prev ? { ...prev, completed_onboarding: completed } : null);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
      throw error;
    }
  };

  return {
    preferences,
    loading,
    savePreferences,
    updateOnboardingStatus,
    hasCompletedOnboarding: preferences?.completed_onboarding || false
  };
};